import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/registry';
import { cn } from '../lib/cn';
import { disabledStyles, focusRing, focusRingOnDark } from '../lib/styles';
import type { IconSize } from '../tokens';

/**
 * Button variants, straight from the prototype.
 *
 * | variant        | where it appears                                          |
 * |----------------|-----------------------------------------------------------|
 * | primary        | the one green action per view                             |
 * | outline        | secondary action on a light surface                       |
 * | onDark         | secondary action in a dark header or on a dark card       |
 * | dark           | "Volgend onderdeel", "Vraag stellen", "Foto toevoegen"    |
 * | accentSoft     | sweep ("Geen gebreken hier"), "Versturen" on the client page |
 * | accentOutline  | "Negeren" on the suggestion card                          |
 * | warn           | "Doorvoeren" on the planning advice bar                   |
 * | light          | white button on a dark header ("Naar EP-online")          |
 * | onDarkSolid    | "Menu" in the narrow top bar                              |
 * | ghost          | inline text action ("Alles bekijken")                     |
 *
 * Never more than one `primary` in a header.
 */
export type ButtonVariant =
    | 'primary'
    | 'outline'
    | 'onDark'
    | 'dark'
    | 'accentSoft'
    | 'accentOutline'
    | 'warn'
    | 'light'
    | 'onDarkSolid'
    | 'ghost';

/**
 * Heights, in px: xs 38 · sm 40 · compact 42 · md 44 · lg 46 · xl 48.
 * `md` (44) is the minimum inside opname and on the public page.
 */
export type ButtonSize = 'xs' | 'sm' | 'compact' | 'md' | 'lg' | 'xl';

/** Every variant sets its own colour. Nothing inherits — white-on-white is the classic bug here. */
const variantStyles: Record<ButtonVariant, string> = {
    primary:
        'border-0 bg-[var(--accent)] text-[var(--accent-text)] font-medium hover:bg-[var(--accent-hover)]',
    outline:
        'border border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:border-[var(--accent)] hover:text-[var(--accent)]',
    onDark: 'border border-[var(--ink-500)] bg-transparent text-[var(--text-on-dark-2)] hover:bg-[var(--ink-hover-on-dark)]',
    dark: 'border-0 bg-[var(--ink-800)] text-[var(--text-on-dark)] font-medium hover:bg-[var(--ink-600)]',
    accentSoft:
        'border border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--accent-dark)] font-medium hover:bg-[var(--accent-tint-hover)]',
    accentOutline:
        'border border-[var(--accent-outline-border)] bg-transparent text-[var(--accent-dark)] hover:bg-[var(--accent-outline-hover)]',
    warn: 'border-0 bg-[var(--warn)] text-[var(--text-on-dark)] hover:bg-[var(--warn-hover)]',
    light: 'border-0 bg-[var(--surface-card)] text-[var(--ink-800)] font-medium hover:bg-[var(--text-on-dark-soft)]',
    onDarkSolid:
        'border border-[var(--ink-500)] bg-[var(--ink-800)] text-[var(--text-on-dark)] font-medium hover:bg-[var(--ink-hover-on-dark)]',
    ghost: 'border-0 bg-transparent p-0 text-[var(--accent)] hover:text-[var(--accent-dark)] hover:underline',
};

/** Sizes carry height, padding, text size and radius together — never mix and match. */
const sizeStyles: Record<ButtonSize, string> = {
    xs: 'min-h-[38px] px-3 py-[9px] text-[12px] rounded-[6px]',
    sm: 'min-h-[40px] px-[13px] py-[10px] text-[13px] rounded-[7px]',
    compact: 'min-h-[42px] px-4 py-[10px] text-[13px] rounded-[7px]',
    md: 'min-h-[44px] px-4 py-[11px] text-[13px] rounded-[7px]',
    lg: 'min-h-[46px] px-[18px] py-[13px] text-[14px] rounded-[8px]',
    xl: 'min-h-[48px] px-5 py-[14px] text-[14px] rounded-[8px]',
};

/** Variants that live on a dark surface and need the white focus outline. */
const onDarkVariants = new Set<ButtonVariant>(['onDark', 'onDarkSolid', 'light']);

/** The icon grows with the button: 16px up to `compact`, 18px from `md`. */
const iconSizeFor: Record<ButtonSize, IconSize> = {
    xs: 'sm',
    sm: 'sm',
    compact: 'sm',
    md: 'md',
    lg: 'md',
    xl: 'md',
};

export type ButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Stretch to the container width — login, "Naar facturatie", "Tarief toevoegen". */
    fullWidth?: boolean;
    /**
     * A line icon before the label, from the brand sheet ("Nieuwe keuring" gets
     * `add`, "Exporteren" gets `download`). Decorative: the label names the action.
     */
    icon?: IconName;
    children: ReactNode;
};

export function Button({
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    icon,
    className,
    type = 'button',
    children,
    ...rest
}: ButtonProps) {
    const ghost = variant === 'ghost';

    return (
        <button
            type={type}
            className={cn(
                'inline-flex items-center justify-center gap-2 whitespace-nowrap',
                'font-[family-name:var(--font-body)] leading-none cursor-pointer',
                'transition-colors duration-100',
                ghost ? 'min-h-0 rounded-[2px] text-[13px]' : sizeStyles[size],
                variantStyles[variant],
                fullWidth && 'w-full',
                onDarkVariants.has(variant) ? focusRingOnDark : focusRing,
                disabledStyles,
                className,
            )}
            {...rest}
        >
            {icon ? <Icon name={icon} size={iconSizeFor[size]} className="-ml-[2px]" /> : null}
            {children}
        </button>
    );
}
